import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { supabase } from './supabaseClient';

function monthLabels() {
  const now = new Date();
  const y = now.getFullYear();
  const m0 = now.getMonth();
  const keys = [];
  for (let i = -5; i <= 3; i++) {
    const d = new Date(y, m0 + i, 1);
    const ys = d.getFullYear();
    const ms = String(d.getMonth() + 1).padStart(2, '0');
    keys.push(`${ys}-${ms}-01`);
  }
  return keys;
}

function formatMonthLabel(periodStart) {
  if (!periodStart) return '—';
  try {
    const d = new Date(`${periodStart}T12:00:00`);
    if (Number.isNaN(d.getTime())) return periodStart;
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return periodStart;
  }
}

function dueDateForPeriod(periodStart, rentDueDay) {
  const d0 = new Date(`${periodStart}T12:00:00`);
  if (Number.isNaN(d0.getTime())) return periodStart;
  const y = d0.getFullYear();
  const mo = d0.getMonth();
  const last = new Date(y, mo + 1, 0).getDate();
  const day = Math.min(Math.max(1, Number(rentDueDay) || 1), last);
  const due = new Date(y, mo, day);
  const ys = due.getFullYear();
  const ms = String(due.getMonth() + 1).padStart(2, '0');
  const ds = String(due.getDate()).padStart(2, '0');
  return `${ys}-${ms}-${ds}`;
}

function propertyFromRow(row) {
  const rel = row?.properties;
  return Array.isArray(rel) ? rel[0] : rel;
}

function propertyLine(row) {
  const p = propertyFromRow(row);
  if (!p?.name) return 'No property';
  return p.location ? `${p.name} — ${p.location}` : p.name;
}

function defaultAmountDue(row) {
  const p = propertyFromRow(row);
  const n = Number(p?.price_per_month);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

const STATUS_OPTIONS = ['pending', 'partial', 'paid', 'waived'];

function monthShortLabel(periodStart) {
  if (!periodStart) return '—';
  try {
    const d = new Date(`${periodStart}T12:00:00`);
    if (Number.isNaN(d.getTime())) return periodStart;
    return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  } catch {
    return periodStart;
  }
}

function inferMonthState(row) {
  if (!row) return 'empty';
  const due = Number(row.amount_due) || 0;
  const paid = Number(row.amount_paid) || 0;
  if (row.status === 'paid' || row.status === 'waived') return 'settled';
  if (row.status === 'partial' || (paid > 0 && paid < due)) return 'partial';
  return 'due';
}

function remainingOnRow(row) {
  if (!row) return null;
  if (row.status === 'waived') return null;
  const due = Number(row.amount_due) || 0;
  const paid = Number(row.amount_paid) || 0;
  return Math.max(0, due - paid);
}

function tenantWindowMoney(tenantId, monthKeys, periods) {
  const keySet = new Set(monthKeys);
  let collected = 0;
  let stillOwed = 0;
  for (const p of periods || []) {
    if (p.prospective_tenant_id !== tenantId || !keySet.has(p.period_start)) continue;
    const due = Number(p.amount_due) || 0;
    const paid = Number(p.amount_paid) || 0;
    collected += paid;
    if (p.status === 'waived') continue;
    stillOwed += Math.max(0, due - paid);
  }
  return { collected, stillOwed };
}

function summarizeThisMonth(tenantId, thisMonthKey, periodByTenantMonth) {
  const row = periodByTenantMonth.get(`${tenantId}|${thisMonthKey}`);
  if (!row) {
    return {
      label: 'No record',
      sub: 'Add this month below',
      badgeClass: 'rent-mgmt-badge rent-mgmt-badge--neutral',
    };
  }
  const due = Number(row.amount_due) || 0;
  const paid = Number(row.amount_paid) || 0;
  if (row.status === 'paid') {
    return {
      label: 'Paid',
      sub: `£${paid} received`,
      badgeClass: 'rent-mgmt-badge rent-mgmt-badge--paid',
    };
  }
  if (row.status === 'waived') {
    return {
      label: 'Waived',
      sub: due ? `£${due} waived` : 'Waived',
      badgeClass: 'rent-mgmt-badge rent-mgmt-badge--waived',
    };
  }
  if (row.status === 'partial' || (paid > 0 && paid < due)) {
    return {
      label: 'Partial',
      sub: `£${paid} of £${due}`,
      badgeClass: 'rent-mgmt-badge rent-mgmt-badge--partial',
    };
  }
  return {
    label: 'Unpaid',
    sub: due ? `£0 of £${due} due` : 'Set amount due',
    badgeClass: 'rent-mgmt-badge rent-mgmt-badge--due',
  };
}

function RentMonthCard({
  tenant,
  periodStart,
  row,
  isCurrentMonth,
  createKey,
  creating,
  savingPeriod,
  createPeriod,
  updateLocalPeriod,
  savePeriod,
  addRentDraft,
  setAddRentDraft,
  addRentPayment,
  addingRent,
}) {
  if (!row) {
    return (
      <div
        className={`tenant-rent-month-card${isCurrentMonth ? ' tenant-rent-month-card--current' : ''}`}
      >
        <div className="tenant-rent-month-card-head">
          <span className="tenant-rent-month-card-title">{formatMonthLabel(periodStart)}</span>
          {isCurrentMonth ? <span className="tenant-rent-month-pill">This month</span> : null}
        </div>
        <p className="tenant-rent-month-card-hint">No rent row yet for this month.</p>
        <button
          type="button"
          className="btn-ghost tenants-btn tenant-rent-month-card-btn"
          disabled={creating[createKey]}
          onClick={() => createPeriod(tenant, periodStart)}
        >
          {creating[createKey] ? 'Adding…' : 'Add month'}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`tenant-rent-month-card${isCurrentMonth ? ' tenant-rent-month-card--current' : ''}`}
    >
      <div className="tenant-rent-month-card-head">
        <span className="tenant-rent-month-card-title">{formatMonthLabel(row.period_start)}</span>
        <span className={`tenant-rent-mini-state tenant-rent-mini-state--${inferMonthState(row)}`}>
          {inferMonthState(row) === 'settled' ? 'Paid' : inferMonthState(row) === 'partial' ? 'Partial' : 'Due'}
        </span>
      </div>
      <div className="tenant-rent-month-fields">
        <label className="tenant-rent-field">
          <span className="tenant-rent-field-label">Due date</span>
          <input
            type="date"
            className="input-element tenant-rent-field-input"
            value={row.due_date || ''}
            onChange={(e) => updateLocalPeriod(row.id, { due_date: e.target.value })}
          />
        </label>
        <label className="tenant-rent-field">
          <span className="tenant-rent-field-label">Due (£)</span>
          <input
            type="number"
            min={0}
            className="input-element tenant-rent-field-input"
            value={row.amount_due}
            onChange={(e) =>
              updateLocalPeriod(row.id, {
                amount_due: parseInt(e.target.value, 10) || 0,
              })
            }
          />
        </label>
        <label className="tenant-rent-field">
          <span className="tenant-rent-field-label">Paid (£)</span>
          <input
            type="number"
            min={0}
            className="input-element tenant-rent-field-input"
            value={row.amount_paid}
            onChange={(e) =>
              updateLocalPeriod(row.id, {
                amount_paid: parseInt(e.target.value, 10) || 0,
              })
            }
          />
        </label>
        <label className="tenant-rent-field tenant-rent-field--full">
          <span className="tenant-rent-field-label">Status</span>
          <select
            className="input-element tenant-rent-field-input"
            value={STATUS_OPTIONS.includes(row.status) ? row.status : 'pending'}
            onChange={(e) => updateLocalPeriod(row.id, { status: e.target.value })}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>
      {row.status === 'waived' ? (
        <p className="rent-mgmt-balance-line rent-mgmt-balance-line--muted">This month is waived — no balance.</p>
      ) : (
        <>
          <p className="rent-mgmt-balance-line">
            <strong>£{Number(row.amount_paid) || 0}</strong> collected ·{' '}
            <strong>£{remainingOnRow(row) ?? 0}</strong> left to match due
          </p>
          <div className="rent-mgmt-quick-add">
            <span className="rent-mgmt-quick-add-label">Add rent (£)</span>
            <input
              type="number"
              min={1}
              className="input-element rent-mgmt-quick-add-input"
              placeholder="e.g. 200"
              value={addRentDraft[row.id] ?? ''}
              onChange={(e) =>
                setAddRentDraft((d) => ({ ...d, [row.id]: e.target.value }))
              }
            />
            <button
              type="button"
              className="btn-ghost tenants-btn"
              disabled={addingRent[row.id] || savingPeriod[row.id]}
              onClick={() => addRentPayment(row)}
            >
              {addingRent[row.id] ? 'Adding…' : 'Add to paid'}
            </button>
          </div>
        </>
      )}
      <button
        type="button"
        className="btn-primary tenants-btn tenant-rent-month-card-btn"
        disabled={savingPeriod[row.id]}
        onClick={() => savePeriod(row)}
      >
        {savingPeriod[row.id] ? 'Saving…' : 'Save month'}
      </button>
    </div>
  );
}

function currentPeriodStartKey() {
  const now = new Date();
  const ys = now.getFullYear();
  const ms = String(now.getMonth() + 1).padStart(2, '0');
  return `${ys}-${ms}-01`;
}

function RentManagement() {
  const monthKeys = React.useMemo(() => monthLabels(), []);
  const thisMonthKey = React.useMemo(() => currentPeriodStartKey(), []);
  const [tenants, setTenants] = React.useState([]);
  const [periods, setPeriods] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [dueDayDraft, setDueDayDraft] = React.useState({});
  const [savingDue, setSavingDue] = React.useState({});
  const [savingPeriod, setSavingPeriod] = React.useState({});
  const [creating, setCreating] = React.useState({});
  const [addRentDraft, setAddRentDraft] = React.useState({});
  const [addingRent, setAddingRent] = React.useState({});

  const periodByTenantMonth = React.useMemo(() => {
    const m = new Map();
    (periods || []).forEach((p) => {
      const k = `${p.prospective_tenant_id}|${p.period_start}`;
      m.set(k, p);
    });
    return m;
  }, [periods]);

  const loadData = React.useCallback(async () => {
    if (!supabase) {
      setError('Supabase is not configured.');
      setTenants([]);
      setPeriods([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    const { data: tRows, error: tErr } = await supabase
      .from('prospective_tenants')
      .select(
        `
        id,
        full_name,
        email,
        rent_due_day_of_month,
        properties (
          name,
          location,
          price_per_month
        )
      `
      )
      .order('created_at', { ascending: false });

    if (tErr) {
      setError(tErr.message);
      setTenants([]);
      setPeriods([]);
      setLoading(false);
      return;
    }

    const list = tRows || [];
    setTenants(list);
    const draft = {};
    list.forEach((r) => {
      draft[r.id] = String(r.rent_due_day_of_month ?? 1);
    });
    setDueDayDraft(draft);

    const ids = list.map((r) => r.id);
    if (ids.length === 0) {
      setPeriods([]);
      setLoading(false);
      return;
    }

    const minMonth = monthKeys[0];
    const { data: pRows, error: pErr } = await supabase
      .from('tenant_rent_periods')
      .select('*')
      .in('prospective_tenant_id', ids)
      .gte('period_start', minMonth)
      .order('period_start', { ascending: false });

    if (pErr) {
      setError(
        `${pErr.message} — Run sql/add_tenant_rent_management_supabase.sql in Supabase if this table is missing.`
      );
      setPeriods([]);
    } else {
      setPeriods(pRows || []);
    }
    setLoading(false);
  }, [monthKeys]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const saveDueDay = async (tenantId) => {
    if (!supabase) return;
    const raw = parseInt(String(dueDayDraft[tenantId] || '1'), 10);
    const day = Number.isFinite(raw) ? Math.min(28, Math.max(1, raw)) : 1;
    setSavingDue((s) => ({ ...s, [tenantId]: true }));
    try {
      const { error: uErr } = await supabase
        .from('prospective_tenants')
        .update({ rent_due_day_of_month: day })
        .eq('id', tenantId);
      if (uErr) throw uErr;
      await loadData();
    } catch (e) {
      setError(e?.message || 'Could not save due day.');
    } finally {
      setSavingDue((s) => ({ ...s, [tenantId]: false }));
    }
  };

  const createPeriod = async (tenant, periodStart) => {
    if (!supabase) return;
    const day = Number(tenant.rent_due_day_of_month) || 1;
    const amount = defaultAmountDue(tenant);
    const due = dueDateForPeriod(periodStart, day);
    const key = `${tenant.id}|${periodStart}`;
    setCreating((c) => ({ ...c, [key]: true }));
    try {
      const { error: iErr } = await supabase.from('tenant_rent_periods').insert({
        prospective_tenant_id: tenant.id,
        period_start: periodStart,
        amount_due: amount,
        amount_paid: 0,
        due_date: due,
        status: 'pending',
        notes: '',
      });
      if (iErr) throw iErr;
      await loadData();
    } catch (e) {
      setError(e?.message || 'Could not create rent period.');
    } finally {
      setCreating((c) => ({ ...c, [key]: false }));
    }
  };

  const savePeriod = async (row) => {
    if (!supabase) return;
    setSavingPeriod((s) => ({ ...s, [row.id]: true }));
    try {
      const isPaid = row.status === 'paid';
      const { error: uErr } = await supabase
        .from('tenant_rent_periods')
        .update({
          amount_due: Number(row.amount_due) || 0,
          amount_paid: Number(row.amount_paid) || 0,
          due_date: row.due_date,
          status: row.status,
          paid_at: isPaid ? row.paid_at || new Date().toISOString() : null,
          notes: row.notes || '',
        })
        .eq('id', row.id);
      if (uErr) throw uErr;
      await loadData();
    } catch (e) {
      setError(e?.message || 'Could not save period.');
    } finally {
      setSavingPeriod((s) => ({ ...s, [row.id]: false }));
    }
  };

  const addRentPayment = async (row) => {
    if (!supabase || row.status === 'waived') return;
    const raw = parseInt(String(addRentDraft[row.id] || ''), 10);
    if (!Number.isFinite(raw) || raw <= 0) {
      setError('Enter a positive amount in “Add rent” to record a payment.');
      return;
    }
    setError('');
    setAddingRent((s) => ({ ...s, [row.id]: true }));
    try {
      const due = Number(row.amount_due) || 0;
      const newPaid = (Number(row.amount_paid) || 0) + raw;
      let status = row.status;
      if (due > 0 && newPaid >= due) status = 'paid';
      else if (newPaid > 0 && newPaid < due) status = 'partial';
      const isPaid = status === 'paid';
      const { error: uErr } = await supabase
        .from('tenant_rent_periods')
        .update({
          amount_due: due,
          amount_paid: newPaid,
          due_date: row.due_date,
          status,
          paid_at: isPaid ? row.paid_at || new Date().toISOString() : null,
          notes: row.notes || '',
        })
        .eq('id', row.id);
      if (uErr) throw uErr;
      setAddRentDraft((d) => ({ ...d, [row.id]: '' }));
      await loadData();
    } catch (e) {
      setError(e?.message || 'Could not add rent payment.');
    } finally {
      setAddingRent((s) => ({ ...s, [row.id]: false }));
    }
  };

  const updateLocalPeriod = (id, patch) => {
    setPeriods((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p))
    );
  };

  return (
    <div className="tenants-root rent-mgmt-page">
      <UniversalHeader />

      <div className="tenants-shell rent-mgmt-shell">
        <header className="tenants-header rent-mgmt-header">
          <div className="rent-mgmt-header-text">
            <h1 className="tenants-title">Rent management</h1>
            <p className="tenants-subtitle">
              Set the rent due day for each tenant, then record monthly amounts and due dates. Use{' '}
              <strong>Add rent</strong> to bump the paid total without retyping the full amount. Summary shows how
              much is collected vs still owed in the months shown below.
            </p>
          </div>
          <div className="tenants-actions">
            <button type="button" className="btn-ghost tenants-btn" onClick={loadData} disabled={loading}>
              Refresh
            </button>
          </div>
        </header>

        {error ? (
          <p className="properties-save-error" role="alert">
            {error}
          </p>
        ) : null}

        {loading ? (
          <p className="properties-list-empty">Loading…</p>
        ) : tenants.length === 0 ? (
          <p className="properties-list-empty">
            No prospective tenants yet. Add tenants under Prospective tenants, then run{' '}
            <code>sql/add_tenant_rent_management_supabase.sql</code> in Supabase.
          </p>
        ) : (
          <>
            <section className="rent-mgmt-overview" aria-labelledby="rent-mgmt-glance-heading">
              <h2 id="rent-mgmt-glance-heading" className="rent-mgmt-overview-title">
                At a glance — who has paid (this month)
              </h2>
              <p className="rent-mgmt-overview-hint">
                Dots show recent months (oldest left). Green = paid or waived, amber = partial, red = unpaid, grey
                = no row yet.
              </p>
              <div className="rent-mgmt-overview-grid">
                {tenants.map((t) => {
                  const glance = summarizeThisMonth(t.id, thisMonthKey, periodByTenantMonth);
                  const win = tenantWindowMoney(t.id, monthKeys, periods);
                  return (
                    <div key={`glance-${t.id}`} className="rent-mgmt-glance-card">
                      <div className="rent-mgmt-glance-top">
                        <span className="rent-mgmt-glance-name">{t.full_name || '—'}</span>
                        <span className={glance.badgeClass}>{glance.label}</span>
                      </div>
                      <p className="rent-mgmt-glance-sub">{glance.sub}</p>
                      <p className="rent-mgmt-glance-money">
                        In view: <strong>£{win.collected.toLocaleString()}</strong> added (paid) ·{' '}
                        <strong>£{win.stillOwed.toLocaleString()}</strong> left to collect
                      </p>
                      <div className="rent-mgmt-mini-months" role="list" aria-label="Recent months">
                        {monthKeys.map((mk) => {
                          const row = periodByTenantMonth.get(`${t.id}|${mk}`);
                          const st = inferMonthState(row);
                          const tip = row
                            ? `${monthShortLabel(mk)}: £${Number(row.amount_paid) || 0}/£${Number(row.amount_due) || 0}`
                            : `${monthShortLabel(mk)}: no record`;
                          return (
                            <span
                              key={mk}
                              role="listitem"
                              className={`rent-mgmt-dot rent-mgmt-dot--${st}${mk === thisMonthKey ? ' rent-mgmt-dot--now' : ''}`}
                              title={tip}
                            >
                              <span className="rent-mgmt-dot-label">{monthShortLabel(mk)}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <div className="tenants-grid rent-mgmt-tenant-grid">
            {tenants.map((t) => {
              const glance = summarizeThisMonth(t.id, thisMonthKey, periodByTenantMonth);
              const win = tenantWindowMoney(t.id, monthKeys, periods);
              return (
              <article className="tenants-card rent-mgmt-tenant-card" key={t.id} style={{ alignItems: 'stretch' }}>
                <div className="tenants-card-main rent-mgmt-card-main" style={{ flexDirection: 'column', gap: 12 }}>
                  <div>
                    <h2 className="tenants-card-name">{t.full_name || '—'}</h2>
                    <p className="tenants-card-contact">{t.email || '—'}</p>
                    <p className="tenants-card-property">{propertyLine(t)}</p>
                  </div>

                  <div className="rent-mgmt-tenant-strip" aria-label="This month status for this tenant">
                    <span className={glance.badgeClass}>{glance.label}</span>
                    <span className="rent-mgmt-tenant-strip-text">
                      {glance.sub}
                      <span className="rent-mgmt-tenant-strip-money">
                        {' '}
                        · £{win.collected.toLocaleString()} in · £{win.stillOwed.toLocaleString()} left
                      </span>
                    </span>
                  </div>

                  <div className="properties-room-row rent-mgmt-due-row">
                    <div className="field-wrapper">
                      <label className="field-label" htmlFor={`due-${t.id}`}>
                        Rent due each month (day 1–28)
                      </label>
                      <div className="input-shell" style={{ maxWidth: 120 }}>
                        <input
                          id={`due-${t.id}`}
                          className="input-element"
                          type="number"
                          min={1}
                          max={28}
                          value={dueDayDraft[t.id] ?? '1'}
                          onChange={(e) =>
                            setDueDayDraft((d) => ({ ...d, [t.id]: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <div className="field-wrapper" style={{ alignSelf: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn-primary tenants-btn"
                        disabled={savingDue[t.id]}
                        onClick={() => saveDueDay(t.id)}
                      >
                        {savingDue[t.id] ? 'Saving…' : 'Save due day'}
                      </button>
                    </div>
                  </div>

                  <div className="tenant-rent-table-wrap tenant-rent-admin-desktop">
                    <table className="tenant-rent-admin-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Due date</th>
                          <th>Due (£)</th>
                          <th>Paid (£)</th>
                          <th>Left (£)</th>
                          <th>Status</th>
                          <th>Add rent</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {monthKeys.map((periodStart) => {
                          const k = `${t.id}|${periodStart}`;
                          const row = periodByTenantMonth.get(k);
                          const createKey = k;
                          if (!row) {
                            return (
                              <tr
                                key={k}
                                className={
                                  periodStart === thisMonthKey
                                    ? 'tenant-rent-admin-row--current'
                                    : ''
                                }
                              >
                                <td>{formatMonthLabel(periodStart)}</td>
                                <td colSpan={5}>
                                  <span className="tenants-card-property">
                                    No record yet — use Add month (right) to start tracking due and paid.
                                  </span>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn-ghost tenants-btn"
                                    disabled={creating[createKey]}
                                    onClick={() => createPeriod(t, periodStart)}
                                  >
                                    {creating[createKey] ? '…' : 'Add month'}
                                  </button>
                                </td>
                                <td>—</td>
                              </tr>
                            );
                          }
                          return (
                            <tr
                              key={row.id}
                              className={
                                row.period_start === thisMonthKey
                                  ? 'tenant-rent-admin-row--current'
                                  : ''
                              }
                            >
                              <td>{formatMonthLabel(row.period_start)}</td>
                              <td>
                                <input
                                  type="date"
                                  className="input-element"
                                  style={{ minWidth: 140 }}
                                  value={row.due_date || ''}
                                  onChange={(e) =>
                                    updateLocalPeriod(row.id, { due_date: e.target.value })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min={0}
                                  className="input-element"
                                  style={{ width: 100 }}
                                  value={row.amount_due}
                                  onChange={(e) =>
                                    updateLocalPeriod(row.id, {
                                      amount_due: parseInt(e.target.value, 10) || 0,
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min={0}
                                  className="input-element"
                                  style={{ width: 100 }}
                                  value={row.amount_paid}
                                  onChange={(e) =>
                                    updateLocalPeriod(row.id, {
                                      amount_paid: parseInt(e.target.value, 10) || 0,
                                    })
                                  }
                                />
                              </td>
                              <td className="rent-mgmt-balance-cell">
                                {row.status === 'waived' ? (
                                  <span className="tenants-card-property">Waived</span>
                                ) : (
                                  <span className="rent-mgmt-left-figure">£{(remainingOnRow(row) ?? 0).toLocaleString()}</span>
                                )}
                              </td>
                              <td>
                                <select
                                  className="input-element"
                                  value={STATUS_OPTIONS.includes(row.status) ? row.status : 'pending'}
                                  onChange={(e) =>
                                    updateLocalPeriod(row.id, { status: e.target.value })
                                  }
                                >
                                  {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                {row.status === 'waived' ? (
                                  <span className="tenants-card-property">—</span>
                                ) : (
                                  <div className="rent-mgmt-quick-add rent-mgmt-quick-add--table">
                                    <input
                                      type="number"
                                      min={1}
                                      className="input-element"
                                      style={{ width: 72 }}
                                      placeholder="+£"
                                      value={addRentDraft[row.id] ?? ''}
                                      onChange={(e) =>
                                        setAddRentDraft((d) => ({ ...d, [row.id]: e.target.value }))
                                      }
                                    />
                                    <button
                                      type="button"
                                      className="btn-ghost tenants-btn"
                                      disabled={addingRent[row.id] || savingPeriod[row.id]}
                                      onClick={() => addRentPayment(row)}
                                    >
                                      {addingRent[row.id] ? '…' : 'Add'}
                                    </button>
                                  </div>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn-primary tenants-btn"
                                  disabled={savingPeriod[row.id]}
                                  onClick={() => savePeriod(row)}
                                >
                                  {savingPeriod[row.id] ? '…' : 'Save'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="tenant-rent-admin-mobile">
                    <h3 className="tenant-rent-mobile-heading">Monthly breakdown</h3>
                    <div className="tenant-rent-month-stack">
                      {monthKeys.map((periodStart) => {
                        const k = `${t.id}|${periodStart}`;
                        const row = periodByTenantMonth.get(k);
                        return (
                          <RentMonthCard
                            key={k}
                            tenant={t}
                            periodStart={periodStart}
                            row={row}
                            isCurrentMonth={periodStart === thisMonthKey}
                            createKey={k}
                            creating={creating}
                            savingPeriod={savingPeriod}
                            createPeriod={createPeriod}
                            updateLocalPeriod={updateLocalPeriod}
                            savePeriod={savePeriod}
                            addRentDraft={addRentDraft}
                            setAddRentDraft={setAddRentDraft}
                            addRentPayment={addRentPayment}
                            addingRent={addingRent}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </article>
            );
            })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default RentManagement;
